/**
 * Web component: Tooltip popup (tooltip-popup)
 * -----------------------------------------------------------------------------
 * Tooltip pop-up.
 *
 */
import './tooltip-popup.scss'

import { vueReady } from '~/core/runners'
import classes from '~/config/classes'

/**
 * Create new <tooltip-popup> custom element.
 */
class TooltipPopup extends HTMLElement {
  static observedAttributes = []

  /**
   * Custom element constructor.
   * - Always call super first in constructor.
   */
  constructor() {
    super()

    this.label = this.querySelector('[js-tooltip-popup="label"]')
    this.tooltip = this.querySelector('[js-tooltip-popup="tooltip"]')
  }

  /**
   * Lifecycle hook - component is attached to DOM.
   */
  connectedCallback() {
    this.label.addEventListener('focusin', this)
    this.label.addEventListener('focusout', this)
    this.label.addEventListener('mouseover', this)
    this.label.addEventListener('touchstart', this, { passive: true })
  }

  /**
   * Lifecycle hook - component is removed from the DOM.
   */
  disconnectedCallback() {
    this.label.removeEventListener('focusin', this)
    this.label.removeEventListener('focusout', this)
    this.label.removeEventListener('mouseover', this)
    this.label.removeEventListener('touchstart', this, { passive: true })
  }

  /**
   * Handle event.
   * @param {Object} event - Event.
   */
  handleEvent(event) {
    switch (event.type) {
      case 'mouseover':
        this.handleDropdownPosition()
        break
      case 'focusin':
      case 'touchstart':
        this.open()
        break
      case 'focusout':
        this.close()
        break
    }
  }

  /**
   * Handle positioning of dropdown.
   */
  handleDropdownPosition() {
    const screenPadding = 16

    const labelRect = this.label.getBoundingClientRect()
    const tooltipRect = this.tooltip.getBoundingClientRect()
    this.tooltip.scroll(0, 0)

    if (tooltipRect.x < 0) {
      this.tooltip.style.left = '0'
      this.tooltip.style.right = 'auto'
      this.tooltip.style.translate = `${-labelRect.x + screenPadding}px 0`

    } else if (tooltipRect.right > window.outerWidth) {
      this.tooltip.style.left = 'auto'
      this.tooltip.style.right = '0'
      this.tooltip.style.translate = `${(window.outerWidth - labelRect.right) - screenPadding}px 0`
    }
  }

  /**
   * Toggle tooltip.
   */
  toggle() {
    if (this.classList.contains(classes.active)) {
      this.close()
      return
    }

    this.open()
  }

  /**
   * Open tooltip.
   */
  open() {
    this.classList.add(classes.active)
    this.handleDropdownPosition()
  }

  /**
   * Close tooltip.
   */
  close() {
    this.classList.remove(classes.active)
  }
}

/**
 * Dismiss all open tooltips.
 * @param {Object} event - Click event.
 */
function dismissAllTooltips(event) {
  if (typeof event.target.closest !== 'function') {
    return
  }

  const currentTooltip = event.target.closest('tooltip-popup')

  document.querySelectorAll('tooltip-popup').forEach((tooltip) => {
    if (tooltip === currentTooltip) {
      return
    }

    tooltip.classList.remove(classes.active)
  })
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('tooltip-popup')) {
    return
  }

  customElements.define('tooltip-popup', TooltipPopup)
  document.addEventListener('touchstart', dismissAllTooltips)
})
